/**
 * Task type / calculated template helpers shared by Settings and Quote Builder.
 */

export const TASK_TEMPLATES = {
  SERVICE_LINE: 'service_line',
  GUTTER_SYSTEM: 'gutter_system',
  ROOFING: 'roofing',
  FLASHING: 'flashing',
  EXCAVATION: 'excavation'
}

export const CUT_FILL_TYPES = [
  { id: 'cut_and_fill', name: 'Dig and Backfill' },
  { id: 'cut', name: 'Dig' },
  { id: 'fill', name: 'Backfill' }
]

/** Excavation method options (Hand + machine sizes). */
export const EXCAVATION_METHODS = [
  { id: 'hand', name: 'Hand' },
  { id: 'mini', name: 'Mini Compact Excavator' },
  { id: 'small', name: 'Small Excavator' },
  { id: 'medium', name: 'Medium Excavator' },
  { id: 'large', name: 'Large Excavator' }
]

/** Soil type options. Concrete/Rock is machine-only. */
export const EXCAVATION_SOIL_TYPES = [
  { id: 'soft', name: 'Soft Soil / Sand' },
  { id: 'medium', name: 'Medium Soil / Silt' },
  { id: 'hard', name: 'Hard Soil / Clay / Tree Roots' },
  { id: 'rock', name: 'Concrete / Rock' }
]

/**
 * Excavation labour rate in minutes per cubic metre, keyed by method then soil type.
 * Derived from real-world reference times. Hand excavation does not support rock.
 */
export const EXCAVATION_RATES = {
  hand: { soft: 200, medium: 300, hard: 400 },
  mini: { soft: 30, medium: 45, hard: 60, rock: 120 },
  small: { soft: 25, medium: 40, hard: 55, rock: 110 },
  medium: { soft: 20, medium: 35, hard: 50, rock: 100 },
  large: { soft: 15, medium: 30, hard: 45, rock: 90 }
}

/** Backfill effort factor applied to excavation time. */
export const BACKFILL_OPS_FACTORS = {
  cut: 1.0,
  fill: 0.5,
  cut_and_fill: 1.5
}

export function excavationMethodName (methodId) {
  const found = EXCAVATION_METHODS.find((m) => m.id === methodId)
  return (found && found.name) || 'Hand'
}

export function soilTypeName (soilId) {
  const found = EXCAVATION_SOIL_TYPES.find((s) => s.id === soilId)
  return (found && found.name) || ''
}

/** Soil type options available for a given excavation method (Hand excludes rock). */
export function soilTypesForMethod (methodId) {
  const rates = EXCAVATION_RATES[methodId] || EXCAVATION_RATES.hand
  return EXCAVATION_SOIL_TYPES.filter((s) => rates[s.id] != null)
}

/** Minutes of labour per cubic metre for a method + soil type. */
export function excavationMinutesPerM3 (methodId, soilId) {
  const rates = EXCAVATION_RATES[methodId] || EXCAVATION_RATES.hand
  const perM3 = rates[soilId]
  if (perM3 != null) return perM3
  // Fall back to medium soil for the method, then hand/medium.
  return rates.medium != null ? rates.medium : EXCAVATION_RATES.hand.medium
}

export function backfillOpsFactor (cutFillType) {
  const factor = BACKFILL_OPS_FACTORS[cutFillType]
  return factor != null ? factor : BACKFILL_OPS_FACTORS.cut
}

/** Dropdown options for Settings > Task Type */
export const TASK_TYPE_OPTIONS = [
  { id: 'time_based', name: 'Timed Task' },
  { id: 'fixed', name: 'Untimed Task' },
  { id: 'service_line', name: 'Service Line' },
  { id: 'gutter_system', name: 'Gutter System' },
  { id: 'roofing', name: 'Roofing' },
  { id: 'flashing', name: 'Flashing' },
  { id: 'excavation', name: 'Excavation' }
]

/** List filter options (Settings list + store) */
export const TASK_TYPE_FILTER_OPTIONS = [
  { id: 'time_based', name: 'Timed Task' },
  { id: 'fixed', name: 'Untimed Task' },
  { id: 'service_line', name: 'Service Line' },
  { id: 'gutter_system', name: 'Gutter System' },
  { id: 'roofing', name: 'Roofing' },
  { id: 'flashing', name: 'Flashing' },
  { id: 'excavation', name: 'Excavation' }
]

const LABEL_MAP = {
  service_line: {
    primary: 'Pipe',
    primaryPlural: 'Pipes',
    fittings: 'Fittings',
    fixings: 'Fixings',
    quantity: 'Metres',
    quantityUnit: 'm',
    difficulty: 'Difficulty',
    hasFittings: true,
    hasFixings: true,
    hasPrimary: true
  },
  gutter_system: {
    primary: 'Gutter',
    primaryPlural: 'Gutters',
    fittings: 'Fittings',
    fixings: 'Fixings',
    quantity: 'Metres',
    quantityUnit: 'm',
    difficulty: 'Difficulty',
    hasFittings: true,
    hasFixings: true,
    hasPrimary: true
  },
  roofing: {
    primary: 'Sheets',
    primaryPlural: 'Sheets',
    fittings: 'Fittings',
    fixings: 'Fixings',
    quantity: 'Metres',
    quantityUnit: 'm',
    difficulty: 'Difficulty',
    hasFittings: true,
    hasFixings: true,
    hasPrimary: true
  },
  flashing: {
    primary: 'Flashing',
    primaryPlural: 'Flashings',
    fittings: 'Fittings',
    fixings: 'Fixings',
    quantity: 'Metres',
    quantityUnit: 'm',
    difficulty: 'Difficulty',
    hasFittings: false,
    hasFixings: true,
    hasPrimary: true
  },
  excavation: {
    primary: 'Excavation',
    primaryPlural: 'Excavation',
    fittings: null,
    fixings: null,
    quantity: 'Volume',
    quantityUnit: 'm³',
    difficulty: 'Soil Type',
    hasFittings: false,
    hasFixings: false,
    hasPrimary: false
  }
}

export function normalizeTemplate (template, type) {
  const t = type === 2 || type === '2' ? 'calculated' : type
  if (t === 'calculated' || t === 2) {
    if (template == null || template === '' || template === 1 || template === '1') {
      return TASK_TEMPLATES.SERVICE_LINE
    }
    if (template === 2 || template === '2') return TASK_TEMPLATES.EXCAVATION
    return String(template)
  }
  return null
}

export function getCalculatedLabels (template) {
  const key = normalizeTemplate(template, 'calculated') || TASK_TEMPLATES.SERVICE_LINE
  return LABEL_MAP[key] || LABEL_MAP.service_line
}

export function isExcavationTemplate (template) {
  return normalizeTemplate(template, 'calculated') === TASK_TEMPLATES.EXCAVATION
}

export function isFlashingTemplate (template) {
  return normalizeTemplate(template, 'calculated') === TASK_TEMPLATES.FLASHING
}

export function isCalculatedType (type) {
  return type === 2 || type === '2' || type === 'calculated'
}

export function isTimedType (type) {
  return type === 1 || type === '1' || type === 'time_based' || type === 'time-based'
}

export function isFixedType (type) {
  return type === 3 || type === '3' || type === 'fixed'
}

/**
 * Map Task Type dropdown id → { type, template } for API.
 */
export function parseTaskTypeSelection (selectionId) {
  if (selectionId === 'time_based' || selectionId === 1 || selectionId === '1') {
    return { type: 'time_based', template: null }
  }
  if (selectionId === 'fixed' || selectionId === 3 || selectionId === '3') {
    return { type: 'fixed', template: null }
  }
  if (selectionId === 'calculated' || selectionId === 2 || selectionId === '2') {
    return { type: 'calculated', template: TASK_TEMPLATES.SERVICE_LINE }
  }
  const templates = Object.values(TASK_TEMPLATES)
  if (templates.includes(selectionId)) {
    return { type: 'calculated', template: selectionId }
  }
  return { type: 'time_based', template: null }
}

/**
 * Build dropdown id from stored type + template.
 */
export function taskTypeSelectionId (type, template) {
  if (isFixedType(type)) return 'fixed'
  if (isTimedType(type)) return 'time_based'
  if (isCalculatedType(type)) {
    return normalizeTemplate(template, 'calculated') || TASK_TEMPLATES.SERVICE_LINE
  }
  return 'time_based'
}

export function formatTaskTypeDisplay (type, template) {
  if (isFixedType(type)) return 'Untimed Task'
  if (isTimedType(type)) return 'Timed Task'
  if (isCalculatedType(type)) {
    const key = normalizeTemplate(template, 'calculated')
    const opt = TASK_TYPE_OPTIONS.find((o) => o.id === key)
    return (opt && opt.name) || 'Calculated'
  }
  return type || ''
}

export function cutFillOpsMultiplier (cutFillType) {
  return cutFillType === 'cut_and_fill' ? 2 : 1
}

export function cutFillDisplayName (cutFillType) {
  const found = CUT_FILL_TYPES.find((t) => t.id === cutFillType)
  return (found && found.name) || 'Dig'
}

/**
 * Soil Type labour multipliers for excavation.
 * Labour minutes = volume (m³) × cut/fill ops × soil multiplier × qty.
 * Medium (1.0x) is the baseline; Soft/Hard scale effort relative to that.
 */
export const SOIL_TYPE_HELP_ITEMS = [
  {
    title: 'Soft Soil / Sand',
    description: 'Sand, loam, or loose fill. Fastest to dig.'
  },
  {
    title: 'Medium Soil / Silt',
    description: 'Average clay or mixed soil. Typical digging effort.'
  },
  {
    title: 'Hard Soil / Clay / Tree Roots',
    description: 'Hard clay, compacted gravel, or tree roots. Slower to dig.'
  },
  {
    title: 'Concrete / Rock',
    description: 'Solid rock or concrete. Machine excavation only — slowest to break out.'
  }
]

/** Help content used by the task-type helper modal. */
export const TASK_TYPE_HELP_ITEMS = [
  {
    emoji: '⏱️',
    title: 'Timed Task',
    description: 'Set completion time × hourly rate (and multiplier).'
  },
  {
    emoji: '💲',
    title: 'Untimed Task',
    description: 'Flat sell / add-on price; no time or rate multiplier.'
  },
  {
    emoji: '👨‍🔧',
    title: 'Service Line',
    description: 'Calculated: Pipe / Fittings / Fixings.'
  },
  {
    emoji: '🌧️',
    title: 'Gutter System',
    description: 'Calculated: Gutter / Fittings / Fixings.'
  },
  {
    emoji: '🏠',
    title: 'Roofing',
    description: 'Calculated: Sheets / Fittings / Fixings.'
  },
  {
    emoji: '🪜',
    title: 'Flashing',
    description: 'Calculated: Flashing / Fixings (no fittings).'
  },
  {
    emoji: '🪏',
    title: 'Excavation',
    description: 'Calculated by Length × Width × Depth with Cut / Fill and Soil Type.'
  }
]

/** Legacy plain-text representation retained for non-modal consumers. */
export const TASK_TYPE_HELP = TASK_TYPE_HELP_ITEMS
  .map(item => `${item.title} — ${item.description}`)
  .join('\n')
